<script setup lang="ts" generic="T extends object">
import ChevronIcon from "@/Components/Frontend/Icons/ChevronIcon.vue";
import { register } from "swiper/element/bundle";

register();
// --- Props
withDefaults(
    defineProps<{
        slides?: T[];
        sliderName?: string;
        navigation?: {
            enabled: boolean;
            nextEl: string;
            prevEl: string;
        };
        thumbs?: T[];
        customArrows?: boolean;
        loop?: boolean;
        freeMode?: boolean;
        centerMode?: boolean;
        slidePerView?: number | string;
        thumbsSlidePerView?: number;
        autoplay?: number;
        autoplayDelay?: number;
        autoplayDelayFirstSlide?: number;
        navigationCustom?: boolean;
        sliderWrapperClass?: string;
        sliderContainerClass?: string;
        sliderClass?: string;
        sliderThumbsContainerClass?: string;
        sliderThumbsClass?: string;
        sliderThumbsGap?: number;
    }>(),
    {
        slides: undefined,
        navigation: undefined,
        thumbs: undefined,
        loop: false,
        slidePerView: 1,
        thumbsSlidePerView: 3,
        autoplay: undefined,
        autoplayDelay: undefined,
        autoplayDelayFirstSlide: undefined,
        sliderThumbsGap: 30,
        navigationCustom: false,
        sliderWrapperClass: "relative select-none",
        sliderContainerClass: undefined,
        sliderName: "slider-ixo",
        sliderThumbsContainerClass: "w-full mt-10",
        sliderClass: "relative aspect-square",
        sliderThumbsClass: "hover:cursor-pointer",
    },
);
// --- Data
// --- Computed
// --- Methods
// --- Watch
// --- Provide
</script>
<template>
    <div :class="sliderWrapperClass">
        <swiper-container
            v-if="slides && slides.length > 0"
            :A11y="{ enabled: false }"
            :slides-per-view="slidePerView"
            :free-mode="freeMode"
            :centered-slides="centerMode"
            :loop="loop"
            :autoplay-disable-on-interaction="autoplayDelay ? false : null"
            :autoplay-pause-on-mouse-enter="autoplayDelay ? true : null"
            :thumbs-swiper="thumbs ? `.thumbs-${sliderName}` : null"
            :navigation="
                navigation ?? {
                    enabled: true,
                    nextEl: `.${sliderName}-next`,
                    prevEl: `.${sliderName}-prev`,
                }
            "
            :class="[sliderName, sliderContainerClass]"
            v-bind="$attrs"
        >
            <swiper-slide
                v-for="(slide, index) in slides"
                :key="index"
                :class="sliderClass"
                :data-swiper-autoplay="autoplayDelay ? (index === 0 ? autoplayDelayFirstSlide : autoplayDelay) : null"
            >
                <slot name="slideContent" :item="slide" :index="index" />
            </swiper-slide>
        </swiper-container>
        <div v-if="!navigationCustom" class="slider-ixo-navigation" :class="`${sliderName}-navigation`">
            <div class="slider-ixo-prev" :class="`${sliderName}-prev`">
                <ChevronIcon v-if="!customArrows" class="rotate-180" />
                <slot v-else name="navigationPrev" />
            </div>
            <div class="slider-ixo-next" :class="`${sliderName}-next`">
                <ChevronIcon v-if="!customArrows" />
                <slot v-else name="navigationNext" />
            </div>
        </div>
        <template v-else>
            <slot name="navigation" />
        </template>
    </div>
    <swiper-container
        v-if="thumbs && thumbs.length > 0"
        :slides-per-view="thumbsSlidePerView"
        :space-between="sliderThumbsGap"
        :class="`thumbs-${sliderName} ${sliderThumbsContainerClass}`"
    >
        <swiper-slide v-for="(slide, index) in slides" :key="index" :class="sliderThumbsClass">
            <slot name="slideThumbContent" :item="slide" :index="index" />
        </swiper-slide>
    </swiper-container>
</template>
<style lang="postcss" scoped>
.slider-ixo-navigation {
    @apply pointer-events-none
    absolute
    left-0
    top-1/2
    z-10
    flex
    w-full
    -translate-y-1/2
    items-center
    justify-between;
}
.slider-ixo-next,
.slider-ixo-prev {
    @apply pointer-events-auto
    hover:cursor-pointer;
}
.slider-ixo {
    @apply w-full;
}
.swiper-button-disabled {
    @apply opacity-0;
}
</style>
